/**
 * Keeps Stage 4's items (`scope`) and the quote's items (`quoteData.scope`) as
 * one set (Peter, 2026-10-23: "Stage 4 should always mirror stage 5 and vice
 * versa. None of them are "Authoritative" over the other.").
 *
 * After any render in which either copy changed, a layout effect gives the
 * other copy the same array before the browser paints, so no view ever shows
 * two versions. Which copy changed decides the direction; when both changed in
 * one render a three-way merge keeps each side's changes
 * (`pairRows`, src/utils/stage4-stage5-mirror.ts).
 *
 * Every stored project holds one set: the old projects were settled once on
 * the server (2026-10-24), and every save pairs the two copies
 * (pairQuoteRowsForSave). A load whose copies print the same items is left
 * exactly as stored until the next real edit. A load whose copies still differ
 * is paired as the settle paired a draft: the quote takes Stage 4's items (its
 * own are archived, so nothing is lost), and an empty Stage 4 takes the
 * quote's items and GST basis.
 *
 * `meta.rowsMirror` marks a quote whose rows follow this rule, and freshness
 * checks then read `meta.rowsReviewedFingerprintHash` instead of "the two
 * copies are equal" (auto-pipeline.js quoteRowsReviewedMatch).
 */
import { useCallback, useLayoutEffect, useRef } from 'react';
import type { Dispatch, MutableRefObject, SetStateAction } from 'react';
import { pairRows, rowsPrintTheSame, withRowsMirrorContract, withRowsNotKept } from '../utils/stage4-stage5-mirror';
import { bindStage5PresentationToScope } from '../utils/project-stage';
import { ROWS_MIRROR_CONTRACT_VERSION } from '../utils/auto-pipeline';
import { ensureItemIds } from '../utils/item-id';

type Row = Record<string, any>;
type Quote = Record<string, any> | null;

export { ROWS_MIRROR_CONTRACT_VERSION };

function loadKeyOf(context: any): string {
    if (!context || typeof context !== 'object') return '';
    return `${context.projectId ?? ''}:${context.loadGeneration ?? ''}`;
}

/**
 * A new project's first save gives it an id without loading anything: the
 * painted context moves from ":N" to "<id>:N" (App.tsx rewrites it when
 * currentProjectId changes, keeping the load generation). That is the same
 * project on screen, so it is re-keyed, never reconciled as a stored pair.
 */
function isNewProjectIdAdoption(previous: string | null, next: string): boolean {
    if (!previous || !previous.startsWith(':')) return false;
    const cut = next.lastIndexOf(':');
    return cut > 0 && next.slice(cut) === previous;
}

export function useStageRowsMirror({
    scope,
    setScope,
    scopeRef,
    quoteData,
    setQuoteData,
    quoteDataRef,
    paintedProjectLoadContextRef,
    setStage4GstInclusive,
}: {
    scope: Row[];
    setScope: Dispatch<SetStateAction<any>>;
    scopeRef: MutableRefObject<any>;
    quoteData: Quote;
    setQuoteData: Dispatch<SetStateAction<any>>;
    quoteDataRef: MutableRefObject<any>;
    paintedProjectLoadContextRef: MutableRefObject<any>;
    /** Stage 4's GST setter: a Stage 4 that takes the quote's items takes its basis. */
    setStage4GstInclusive?: (isGstInclusive: boolean) => void;
}): void {
    const setStage4GstInclusiveRef = useRef(setStage4GstInclusive);
    setStage4GstInclusiveRef.current = setStage4GstInclusive;
    // Each copy as both last agreed (one array once paired).
    const agreedRef = useRef<{ stage4: unknown; stage5: unknown }>({ stage4: null, stage5: null });
    const loadKeyRef = useRef<string | null>(null);
    const currentLoadKey = useCallback(() => loadKeyOf(paintedProjectLoadContextRef?.current), [paintedProjectLoadContextRef]);

    const writePair = useCallback((rows: Row[], quote: Row) => {
        agreedRef.current = { stage4: rows, stage5: rows };
        if (scopeRef.current !== rows) {
            scopeRef.current = rows;
            setScope(rows);
        }
        if (quote !== quoteDataRef.current) {
            quoteDataRef.current = quote;
            setQuoteData(quote);
        }
    }, [quoteDataRef, scopeRef, setQuoteData, setScope]);

    useLayoutEffect(() => {
        const quote = quoteData;
        const rows = Array.isArray(scope) ? scope : [];
        const loadKey = currentLoadKey();
        if (!quote || typeof quote !== 'object' || !Array.isArray(quote.scope)) {
            agreedRef.current = { stage4: rows, stage5: null };
            loadKeyRef.current = loadKey;
            return;
        }
        if (loadKeyRef.current !== loadKey) {
            const previous = loadKeyRef.current;
            loadKeyRef.current = loadKey;
            if (!isNewProjectIdAdoption(previous, loadKey)) {
                if (rowsPrintTheSame(rows, quote.scope)) {
                    // One set as stored: left exactly as it is.
                    agreedRef.current = { stage4: rows, stage5: quote.scope };
                    return;
                }
                // Paired as the settle paired a draft, so the screen shows
                // what the next save stores.
                if (rows.length === 0 && quote.scope.length > 0) {
                    const quoteRows = ensureItemIds(quote.scope);
                    writePair(quoteRows, withRowsMirrorContract(bindStage5PresentationToScope(quote, quoteRows), quoteRows));
                    // The quote's amounts are on the quote's basis.
                    if (typeof quote.isGstInclusive === 'boolean') setStage4GstInclusiveRef.current?.(quote.isGstInclusive);
                } else {
                    const kept = quote.scope.length > 0
                        ? withRowsNotKept(quote, { kept: 'stage4', reason: 'paired-on-open', rows: quote.scope, at: new Date().toISOString() })
                        : quote;
                    writePair(rows, withRowsMirrorContract(kept, rows));
                }
                return;
            }
        }
        if (quote.scope === rows) {
            agreedRef.current = { stage4: rows, stage5: rows };
            return;
        }
        const next = pairRows({
            previousStage4: agreedRef.current.stage4,
            previousStage5: agreedRef.current.stage5,
            stage4: rows,
            stage5: quote.scope,
        });
        if (!next) return;
        writePair(next, withRowsMirrorContract(quote, next));
    }, [scope, quoteData, currentLoadKey, writePair]);
}
